import React, { useState } from 'react';

/**
 * ThingsToDo — Delivery Tracker for Harshad Patel Demo (Monday April 20, 2026)
 *
 * Three environments must pass the same checklist:
 *   ENV-1: localhost:3000
 *   ENV-2: staging.plug-n-play-infra.ai
 *   ENV-3: plug-n-play-infra.ai (prod)
 *
 * For each env: pnp_main, signup, signin, session-timeout, payment, email.
 */

// ─── Data ────────────────────────────────────────────────────────────────────

const TODAY = '2026-04-17';

const ENVIRONMENTS = [
  { id: 'local',   label: 'localhost:3000',                  url: 'http://localhost:3000',                       badge: '🖥️ Local Dev' },
  { id: 'staging', label: 'staging.plug-n-play-infra.ai',    url: 'https://staging.plug-n-play-infra.ai',        badge: '🧪 Staging' },
  { id: 'prod',    label: 'plug-n-play-infra.ai',            url: 'https://plug-n-play-infra.ai',                badge: '🚀 Production' },
];

// Each task has: id, area, title, description, manualSteps, automatedCmd, targetDate, risk
const TASK_TEMPLATES = [
  {
    id: 'pnp_main',
    area: 'pnp_main',
    icon: '🏠',
    title: 'pnp_main loads & auth guard works',
    description: 'Navigate to /pnp_main. Without a valid token the app must redirect to /login — it must NOT let you into the system. With a valid token the operator console loads.',
    manualSteps: [
      'Open browser in incognito (no stored tokens)',
      'Go to {ENV}/pnp_main',
      'EXPECT: redirect to /login (not the dashboard)',
      'Log in via Cognito → EXPECT: operator console loads',
      'Verify sidebar shows "Operator Mode" badge',
    ],
    automatedCmd: 'npx playwright test tests/e2e/auth.spec.js --grep "pnp_main auth guard"',
    targetDate: '2026-04-17',
    risk: 'HIGH',
  },
  {
    id: 'signup',
    area: 'signup',
    icon: '📝',
    title: 'Signup flow end-to-end',
    description: 'New user hits /signup, fills company name + email + product type, submits. Backend creates subscriber session. User lands on /subscriber portal.',
    manualSteps: [
      'Open {ENV}/signup in incognito',
      'Fill: Company Name, Email, Product Type',
      'Click "Sign Up →"',
      'EXPECT: redirect to /subscriber (not an error)',
      'EXPECT: subscriber portal shows company name',
      'Check backend: POST /v1/clients/onboard returned 200',
    ],
    automatedCmd: 'npx playwright test tests/e2e/signup.spec.js',
    targetDate: '2026-04-17',
    risk: 'HIGH',
  },
  {
    id: 'signin',
    area: 'signin',
    icon: '🔑',
    title: 'Sign-in / Cognito login works',
    description: 'Existing user signs in via Cognito Hosted UI. Token stored in localStorage. /pnp_main loads. Wrong password shows error — does NOT let user in.',
    manualSteps: [
      'Go to {ENV}/login (or click "Sign In" on marketing page)',
      'Enter VALID credentials → EXPECT: redirect to /pnp_main',
      'Verify pnp_access_token in localStorage (DevTools → Application)',
      'Log out, enter WRONG password → EXPECT: "Invalid email or password" error',
      'Verify no token stored after failed login',
    ],
    automatedCmd: 'npx playwright test tests/e2e/auth.spec.js --grep "signin"',
    targetDate: '2026-04-18',
    risk: 'HIGH',
  },
  {
    id: 'timeout',
    area: 'session-timeout',
    icon: '⏱️',
    title: 'Session timeout / token expiry blocks access',
    description: 'Cognito access tokens expire in 1 hour. After expiry the app must NOT allow continued access — it must redirect to /login. The localhost bypass must be removed for staging/prod.',
    manualSteps: [
      'Log in, note the token in localStorage',
      'Manually set token to an expired/invalid value in DevTools',
      'Refresh /pnp_main',
      'EXPECT: redirect to /login (not the dashboard)',
      'Verify the isLocalhost bypass in App.js is ONLY active for local env',
      'On staging/prod: confirm expired token = redirect, not access',
    ],
    automatedCmd: 'npx playwright test tests/e2e/auth.spec.js --grep "token expiry"',
    targetDate: '2026-04-18',
    risk: 'HIGH',
  },
  {
    id: 'payment',
    area: 'payment',
    icon: '💳',
    title: 'Stripe payment / validation fee works',
    description: 'Subscriber completes $5 validation checkout. Stripe webhook fires checkout.session.completed. Trial is activated (45 days). Use Stripe test card 4242 4242 4242 4242.',
    manualSteps: [
      'Sign up as new subscriber',
      'Reach the payment step (card validation modal)',
      'Enter Stripe test card: 4242 4242 4242 4242 / 12/28 / 123',
      'Click "Validate Card & Start Free Trial"',
      'EXPECT: success screen, redirect to /subscriber',
      'Check Stripe Dashboard → Payments: $5 charge appears',
      'Check DB: pnp_signups.stripe_subscription_status = "trialing"',
      'Check DB: trial_access_end_at = now + 45 days',
    ],
    automatedCmd: 'npx playwright test tests/e2e/payment.spec.js',
    targetDate: '2026-04-19',
    risk: 'HIGH',
  },
  {
    id: 'email',
    area: 'email',
    icon: '📧',
    title: 'Transactional emails fire correctly',
    description: 'After payment success, a welcome email must arrive. After subscription cancellation, a cancellation email fires. Uses AWS SES or event_dispatcher.py.',
    manualSteps: [
      'Complete payment flow (see payment task above)',
      'Check inbox for welcome email within 2 minutes',
      'EXPECT: subject contains "Welcome to Plug-N-Play-Infra"',
      'EXPECT: email shows trial end date',
      'Simulate payment failure (Stripe test card 4000 0000 0000 0002)',
      'EXPECT: payment failure email arrives',
      'Check server logs: event_dispatcher.py emit_event called',
    ],
    automatedCmd: 'python server/tests/test_email_dispatch.py',
    targetDate: '2026-04-19',
    risk: 'MEDIUM',
  },
];

// Storage key for persisting checkbox state
const STORAGE_KEY = 'pnp_things_todo_v2';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// ─── Component ───────────────────────────────────────────────────────────────

const ThingsToDo = () => {
  const [checked, setChecked] = useState(loadState);
  const [activeEnv, setActiveEnv] = useState('local');
  const [expandedTask, setExpandedTask] = useState(null);
  const [showAutomated, setShowAutomated] = useState(false);

  const toggle = (key) => {
    setChecked(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveState(next);
      return next;
    });
  };

  const checkKey = (envId, taskId, stepIdx) => `${envId}__${taskId}__step${stepIdx}`;
  const taskKey  = (envId, taskId) => `${envId}__${taskId}__done`;

  // Progress per environment
  const envProgress = (envId) => {
    const total = TASK_TEMPLATES.length * 6; // ~6 manual steps each
    let done = 0;
    TASK_TEMPLATES.forEach(t => {
      t.manualSteps.forEach((_, i) => {
        if (checked[checkKey(envId, t.id, i)]) done++;
      });
    });
    return { done, total, pct: Math.round((done / total) * 100) };
  };

  // Overall progress across all envs
  const overallProgress = () => {
    let done = 0, total = 0;
    ENVIRONMENTS.forEach(env => {
      const p = envProgress(env.id);
      done += p.done; total += p.total;
    });
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  const overall = overallProgress();
  const env = ENVIRONMENTS.find(e => e.id === activeEnv);

  const riskColor = (r) => r === 'HIGH' ? '#c62828' : r === 'MEDIUM' ? '#e65100' : '#2e7d32';

  return (
    <div style={{ padding: '24px', maxWidth: '960px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header ── */}
      <div style={{ background: 'linear-gradient(135deg, #1a237e, #283593)', borderRadius: '16px', padding: '24px 28px', color: '#fff', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: 700 }}>📋 Things To Do — Delivery Tracker</h1>
            <p style={{ margin: 0, fontSize: '13px', opacity: 0.85 }}>
              Harshad Patel Demo: <strong>Monday April 20, 2026</strong> &nbsp;|&nbsp; Trial Period: 3–4 months after delivery
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '28px', fontWeight: 800 }}>{overall.pct}%</div>
            <div style={{ fontSize: '12px', opacity: 0.8 }}>{overall.done}/{overall.total} steps done</div>
          </div>
        </div>

        {/* Overall progress bar */}
        <div style={{ marginTop: '16px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', height: '10px' }}>
          <div style={{ width: `${overall.pct}%`, background: overall.pct === 100 ? '#69f0ae' : '#ffeb3b', height: '10px', borderRadius: '8px', transition: 'width 0.4s' }} />
        </div>
      </div>

      {/* ── Timeline Summary ── */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#1a237e' }}>📅 Completion Date Planner</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
          {[
            { date: 'Apr 17 (Today)',  label: 'pnp_main auth guard + Signup flow',    envs: 'All 3 envs',  status: 'IN PROGRESS', color: '#e65100' },
            { date: 'Apr 18 (Sat)',    label: 'Sign-in + Session timeout/expiry',      envs: 'All 3 envs',  status: 'NEXT',        color: '#1565c0' },
            { date: 'Apr 19 (Sun)',    label: 'Payment (Stripe) + Email dispatch',     envs: 'All 3 envs',  status: 'NEXT',        color: '#1565c0' },
            { date: 'Apr 20 (Mon)',    label: '🎯 Harshad Patel Demo — Prod delivery', envs: 'Prod only',   status: 'DEADLINE',    color: '#c62828' },
            { date: 'Apr–Jul 2026',    label: 'Trial period (3–4 months)',             envs: 'Prod',        status: 'TRIAL',       color: '#2e7d32' },
          ].map((row, i) => (
            <div key={i} style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', borderLeft: `4px solid ${row.color}` }}>
              <div style={{ fontWeight: 700, fontSize: '13px', color: row.color }}>{row.status}</div>
              <div style={{ fontWeight: 600, fontSize: '14px', margin: '2px 0' }}>{row.date}</div>
              <div style={{ fontSize: '13px', color: '#333' }}>{row.label}</div>
              <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{row.envs}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '16px', background: '#fff3e0', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#e65100' }}>
          <strong>⏱️ Time estimate:</strong> With focused effort, all 6 areas across 3 environments = <strong>~12–18 hours of work</strong> (Fri–Sun).
          Each area takes ~1–2 hrs per env (manual test + fix + redeploy). Payment + email may need extra time if Stripe webhooks need wiring.
          You have <strong>3 days</strong> before Monday. It's tight but achievable.
        </div>
      </div>

      {/* ── Environment Tabs ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {ENVIRONMENTS.map(e => {
          const p = envProgress(e.id);
          const isActive = activeEnv === e.id;
          return (
            <button
              key={e.id}
              onClick={() => setActiveEnv(e.id)}
              style={{
                padding: '10px 18px', borderRadius: '10px', border: '2px solid',
                borderColor: isActive ? '#1a237e' : '#e0e0e0',
                background: isActive ? '#1a237e' : '#fff',
                color: isActive ? '#fff' : '#333',
                fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              <span>{e.badge}</span>
              <span>{e.label}</span>
              <span style={{
                background: isActive ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
                color: isActive ? '#fff' : '#555',
                borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 700,
              }}>{p.pct}%</span>
            </button>
          );
        })}

        <button
          onClick={() => setShowAutomated(v => !v)}
          style={{
            marginLeft: 'auto', padding: '10px 16px', borderRadius: '10px',
            border: '2px solid #7b1fa2', background: showAutomated ? '#7b1fa2' : '#fff',
            color: showAutomated ? '#fff' : '#7b1fa2', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
          }}
        >
          {showAutomated ? '🤖 Hide Automated' : '🤖 Show Automated Cmds'}
        </button>
      </div>

      {/* ── Task Cards ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {TASK_TEMPLATES.map(task => {
          const allStepsDone = task.manualSteps.every((_, i) => checked[checkKey(activeEnv, task.id, i)]);
          const stepsDone = task.manualSteps.filter((_, i) => checked[checkKey(activeEnv, task.id, i)]).length;
          const isExpanded = expandedTask === `${activeEnv}__${task.id}`;

          return (
            <div
              key={task.id}
              style={{
                background: '#fff', border: `2px solid ${allStepsDone ? '#4caf50' : '#e0e0e0'}`,
                borderRadius: '12px', overflow: 'hidden',
                boxShadow: allStepsDone ? '0 2px 8px rgba(76,175,80,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              {/* Card header */}
              <div
                onClick={() => setExpandedTask(isExpanded ? null : `${activeEnv}__${task.id}`)}
                style={{
                  padding: '16px 20px', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: '12px',
                  background: allStepsDone ? '#f1f8e9' : '#fafafa',
                }}
              >
                <span style={{ fontSize: '22px' }}>{task.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '15px', color: '#1a1a2e' }}>{task.title}</span>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                      background: riskColor(task.risk) + '20', color: riskColor(task.risk),
                    }}>{task.risk} RISK</span>
                    <span style={{ fontSize: '11px', color: '#888' }}>Target: {task.targetDate}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{task.description}</div>
                </div>
                <div style={{ textAlign: 'right', minWidth: '60px' }}>
                  <div style={{ fontWeight: 700, fontSize: '16px', color: allStepsDone ? '#4caf50' : '#1a237e' }}>
                    {stepsDone}/{task.manualSteps.length}
                  </div>
                  <div style={{ fontSize: '11px', color: '#888' }}>steps</div>
                </div>
                <span style={{ fontSize: '18px', color: '#999', marginLeft: '4px' }}>{isExpanded ? '▲' : '▼'}</span>
              </div>

              {/* Progress bar */}
              <div style={{ height: '4px', background: '#f0f0f0' }}>
                <div style={{
                  width: `${(stepsDone / task.manualSteps.length) * 100}%`,
                  height: '4px', background: allStepsDone ? '#4caf50' : '#1a237e',
                  transition: 'width 0.3s',
                }} />
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{ padding: '16px 20px', borderTop: '1px solid #f0f0f0' }}>

                  {/* Environment context */}
                  <div style={{ background: '#e8eaf6', borderRadius: '8px', padding: '8px 14px', marginBottom: '14px', fontSize: '12px', color: '#3949ab' }}>
                    <strong>Testing on:</strong> {env.badge} — <a href={env.url} target="_blank" rel="noreferrer" style={{ color: '#1a237e' }}>{env.url}/{task.area}</a>
                  </div>

                  {/* Manual test steps */}
                  <div style={{ marginBottom: showAutomated ? '14px' : 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#333', marginBottom: '8px' }}>
                      🧪 Manual Test Steps
                    </div>
                    {task.manualSteps.map((step, i) => {
                      const key = checkKey(activeEnv, task.id, i);
                      const done = !!checked[key];
                      const stepText = step.replace('{ENV}', env.url);
                      return (
                        <label
                          key={i}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: '10px',
                            padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
                            background: done ? '#f1f8e9' : 'transparent',
                            marginBottom: '4px',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={done}
                            onChange={() => toggle(key)}
                            style={{ marginTop: '2px', accentColor: '#4caf50', width: '16px', height: '16px', flexShrink: 0 }}
                          />
                          <span style={{
                            fontSize: '13px', color: done ? '#4caf50' : '#333',
                            textDecoration: done ? 'line-through' : 'none',
                          }}>
                            {stepText}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Automated test command */}
                  {showAutomated && (
                    <div style={{ background: '#1a1a2e', borderRadius: '8px', padding: '12px 16px' }}>
                      <div style={{ fontSize: '11px', color: '#90caf9', marginBottom: '6px', fontWeight: 600 }}>
                        🤖 AUTOMATED TEST COMMAND
                      </div>
                      <code style={{ fontSize: '12px', color: '#a5d6a7', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {task.automatedCmd}
                      </code>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Auth Guard Fix Reminder ── */}
      <div style={{ marginTop: '24px', background: '#fff3e0', border: '2px solid #ff9800', borderRadius: '12px', padding: '20px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: '#e65100' }}>⚠️ Critical Code Fix Required Before Monday</h3>
        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#333' }}>
          <strong>App.js auth guard has a localhost bypass</strong> that must NOT apply to staging or prod.
          Currently: <code style={{ background: '#fce4ec', padding: '2px 6px', borderRadius: '4px' }}>if (!token && !isLocalhost)</code> — this is correct for local dev only.
        </p>
        <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#333' }}>
          Verify that on <strong>staging</strong> and <strong>prod</strong>, visiting <code>/pnp_main</code> without a token redirects to <code>/login</code>.
          The <code>isLocalhost</code> check already handles this correctly — just confirm it works end-to-end on each environment.
        </p>
        <div style={{ background: '#1a1a2e', borderRadius: '8px', padding: '12px 16px', marginTop: '10px' }}>
          <code style={{ fontSize: '12px', color: '#a5d6a7', fontFamily: 'monospace' }}>
            {'// App.js — current guard (correct):\n'}
            {'const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";\n'}
            {'const token = localStorage.getItem("pnp_access_token");\n'}
            {'if (!token && !isLocalhost) { window.location.href = "/login"; return null; }'}
          </code>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
        Progress auto-saved in browser localStorage &nbsp;|&nbsp; Last updated: {TODAY} &nbsp;|&nbsp; Plug-N-Play-Infra.ai
      </div>
    </div>
  );
};

export default ThingsToDo;
